import React from 'react';
import _ from 'lodash';
import moment from "moment";
import clsx from "clsx";
import {connect} from "react-redux";

import {makeStyles} from "@material-ui/core";
import Grid from '@material-ui/core/Grid';
import Skeleton from '@material-ui/lab/Skeleton';
import {ErrorOutline} from "@material-ui/icons";

import ChartSectionBordered, {ChartSectionWrapper} from "../components_v2/ChartSectionBordered/ChartSectionBordered";
import DashboardInfoTooltip from "../components_v2/DasboardInfoTooltip";
import useStyles from './styles';
import {useDepotFinancialData} from "../hooks/useDepotFinancialData";
import {
  getErrorMessage,
  isExpertSectionVisible,
  isSectionVisible,
  withEuroOrDash,
  withPercentOrDash
} from '../../../utils/utils';
import {REPORT_TYPES} from "../../DashboardSettings/components/CustomersList/components/ReportType/constants";
import {useCustomerESGProfileData} from "../hooks/useCustomerEsgProfileData";
import { getRiskType } from '../../RiskDashboard/utils';
import { RISK_FILTERING_TYPE } from '../../../components/FilteringPanel/components/RiskFilter/constants';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import { executeIfPathExist, getInvestmentDynamicPath } from '../../InvestmentPlatform/utils';
import EsgSlide from '../components_v2/EsgSlide';
import { ESG_INFO_TEXT, OKO_INFO_TEXT, PAI_INFO_TEXT } from '../../FactSheetsPage/constants';
import { toGermanFormat } from '../../../utils/numberFormater';
import {getInvestmentPlatformSelector, getSharedSettingsSelector} from '../../../utils/redaxSelectors';
import {useUnrealizedProfitAndLossData} from "../hooks/useUnrealizedProfitAndLoss";
import {
  getDataRetrievingParamsFromProps,
  getSharedSettingsWarning,
  isExpanded
} from "../utils";
import { DATE_FORMAT } from '../../../utils/constants';
import ReturnValue
  from '../../../components/PortfolioDashboard/AggregatedPortfolioReturnSection/components/ReturnValue';
import AggregatedPortfolioReturnSection from '../../../components/PortfolioDashboard/AggregatedPortfolioReturnSection';
import ReturnHeader
  from '../../../components/PortfolioDashboard/AggregatedPortfolioReturnSection/components/ReturnHeader';
import WarningTooltip from "../../../components/WarningTooltip";
import {default as OtherAssetsSectionV2} from "../components_v2/OtherAssetsSection";
import {default as OtherAssetsModuleModal} from "../../OtherAssetV2/OtherAssetsModal/index";
import ArrowIconButton from "../components_v2/ArrowIconButton";
import {MENUS} from "../../OtherAssetV2/OtherAssetsModal/constants";
import AmountChip from "../components_v2/AmountChip";
import DetailsButton from "../components_v2/DetailsButton";
import {BadgeForNew} from "../../../components/Badge";


// region Single Portfolio Details
const useSummarySectionValuesStyles = makeStyles(() => ({
  title: {
    fontFamily: 'Roboto-Regular',
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  cell: {
    textAlign: 'right'
  },
  cellEmpty: {
    textAlign: 'center'
  }
}));


const PortfolioSummarySection = React.memo(({customerId, portfolio, startDate, endDate, dataProvider, reportType, customSettings, sharedSettings, expanded, onExpanded}) => {

  const [data] = useDepotFinancialData(customerId, [portfolio], startDate, endDate, true, undefined, dataProvider, false, false, `depot-financial-data-${portfolio.depotNumber}`, false);

  const classes = useSummarySectionValuesStyles();

  const renderSectionValue = (dataKey, field) => {
    const sectionData = _.get(data, `data.data.${dataKey}`);
    const value = _.get(sectionData, field);
    const warning = getSharedSettingsWarning(_.get(sectionData, `${field}_warning`), sharedSettings.data);
    const cellClass = _.isNil(value) ? classes.cellEmpty : classes.cell;

    const valueHandler = {
      'percentage': withPercentOrDash,
      'value': withEuroOrDash,
      'percentage_pa': withPercentOrDash
    }[field];

    return <div className={cellClass}>
      {!!warning
        ? <><b>-</b> <WarningTooltip title={warning}/></>
        : <ReturnValue value={value} valueHandler={valueHandler} />
      }
    </div>
  };

  const renderTitle = () => {
    const name = (<b>{portfolio.name}</b>);
    if (!data.data) {
      return name;
    }

    const depotValue = _.get(data, 'data.data.aum.aum_value') || 0;
    return (
      <div>
        {name} <br />
        <b>{withEuroOrDash(depotValue)}</b>
      </div>
    )
  };

  const trackingStartDate = _.get(data, 'data.data.start_date');

  return (
    <ChartSectionBordered
      title={renderTitle()}
      isPortfolioSection
      borderLeftColor={portfolio.color}
      expanded={expanded}
      onExpanded={onExpanded}
      subSection
      loading={data.loading}
      error={data.error}
      displayError
      content={reportType != REPORT_TYPES.DEPOT_STATUS.value ? (
        <Grid container spacing={4} wrap={"nowrap"} style={{overflowX: 'auto'}}>
          <Grid item>
            <div>&nbsp;</div>
            <div className={classes.title}>Ausgewählter Zeitraum</div>
            <div className={classes.title}>Investmentbeginn ({!!trackingStartDate && moment(trackingStartDate).format(DATE_FORMAT)})</div>
          </Grid>
          {isSectionVisible(customSettings, 'profit_perc', reportType) && (
            <Grid item>
              <div className={clsx(classes.title, classes.cell)}>GuV (%)</div>
              {renderSectionValue('ydt', 'percentage')}
              {renderSectionValue('ysb', 'percentage')}
            </Grid>
          )}
          {isSectionVisible(customSettings, 'profit_amount', reportType) && (
            <Grid item>
              <div className={clsx(classes.title, classes.cell)}>GuV (€)</div>
              {renderSectionValue('ydt', 'value')}
              {renderSectionValue('ysb', 'value')}
            </Grid>
          )}
          {isSectionVisible(customSettings, 'profit_pa', reportType) && (
            <Grid item>
              <div className={clsx(classes.title, classes.cell)}>GuV p.a. (%)</div>
              {renderSectionValue('ydt', 'percentage_pa')}
              {renderSectionValue('ysb', 'percentage_pa')}
            </Grid>
          )}
        </Grid>
      ) : null}
    />
  )
}, (prevProps, nextProps) => {
  return prevProps.customerId == nextProps.customerId
    && JSON.stringify(prevProps.portfolio) == JSON.stringify(nextProps.portfolio)
    && prevProps.startDate == nextProps.startDate
    && prevProps.endDate == nextProps.endDate
    && prevProps.expanded == nextProps.expanded
    && prevProps.reportType == nextProps.reportType;
});
// endregion

function ESGProfileSection({customerId, portfolios, aggregatedPortfolioName, expanded, onExpanded}) {

  const data = useCustomerESGProfileData(customerId, portfolios, aggregatedPortfolioName);
  const esgData = _.get(data.data, 'esg_profile_matching');
  const dataError = data.error;
  const hasProfile = !_.isEmpty(esgData);

  return (
    <ChartSectionBordered
      title={<b>Nachhaltigkeitsprofil</b>}
      titleControl={!dataError &&
        <div style={{alignContent: 'center'}}>
          {data.loading
            ? <Skeleton width={111} />
            : <>Erfüllungsgrad<DashboardInfoTooltip
              title={hasProfile
                ? "Der Wert auf der linken Seite zeigt den Erfüllungsgrad des Portfolios in Prozent an. Der Wert auf der rechten Seite gibt die prozentuale Vorgabe des Kunden aus dem Nachhaltigkeitsprofil an."
                : "Kein Nachhaltigkeitsprofil vorhanden. Ohne ein Nachhaltigkeitsprofil kann der Erfüllungsgrad nicht angezeigt werden."
              }/></>
          }
        </div>
      }
      expanded={expanded}
      onExpanded={onExpanded}
      subSection
      error={dataError}
      displayError
      content={<ESGProfileCard esgData={esgData} loading={data.loading} />}
    />
  )
}

const RiskProfileCard = ({customerRisk, productRisk, loading}) => {
  const classes = useStyles();

  let [label, color] =
    customerRisk ?
      productRisk ? {
        [RISK_FILTERING_TYPE.GREEN.value]: [<><i className={"far fa-check-circle"}/> Angemessenes Risiko</>, 'green'],
        [RISK_FILTERING_TYPE.RED.value]: [<><i className={"far fa-exclamation-triangle"}/> Zu hohes Risiko</>, 'red'],
        [RISK_FILTERING_TYPE.YELLOW.value]: [<><i className={"far fa-exclamation-triangle"}/> Leicht überschrittenes Risiko</>, 'yellow']
      }[getRiskType(customerRisk, productRisk).value]
      : [<><i className={"far fa-question-circle"}/> Unbekannt</>, 'grey']
    : ['Kein Kundenrisikoprofil vorhanden', 'grey'];

  const formatNumber = (value) => value > 0 ? toGermanFormat(value, undefined, undefined, 2, true) : '-';

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {loading ? <Skeleton height={52} /> :
          <div className={clsx(classes.indicatorCard, color)}>
            <span className={classes.hugeNumber}>{label}</span>
          </div>
        }
      </Grid>
      <Grid item xs={12} container>
        <Grid item xs={6}>
          <div>Max. ø Portfoliorisiko</div>
          <div className={classes.hugeNumber}>{loading ? <Skeleton width={50}/> : formatNumber(productRisk)}</div>
        </Grid>
        <Grid item xs={6}>
          <div>Risikoklasse Kunde</div>
          <div className={classes.hugeNumber}>{loading ? <Skeleton width={50}/> : formatNumber(customerRisk)}</div>
        </Grid>
      </Grid>
    </Grid>
  )
};

const ESGProfileCard = ({esgData, loading}) => (
  <Grid container spacing={1}>
    <Grid item xs={12}>
      <EsgSlide
        categoryData={_.get(esgData, 'eco_sustainability')}
        title={<>Ökologisch-nachhaltig<DashboardInfoTooltip title={OKO_INFO_TEXT}/></>}
        loading={loading} />
    </Grid>
    <Grid item xs={12}>
      <EsgSlide
        categoryData={_.get(esgData, 'esg')}
        title={<>ESG-Kriterien<DashboardInfoTooltip title={ESG_INFO_TEXT}/></>}
        loading={loading} />
    </Grid>
    <Grid item xs={12}>
      <EsgSlide
        categoryData={_.get(esgData, 'pai')}
        title={<>PAI-Nachhaltigkeitsfaktoren<DashboardInfoTooltip title={PAI_INFO_TEXT}/></>}
        loading={loading} />
    </Grid>
  </Grid>
);

const mapStateToProps = (state) => ({
  investmentPlatform: getInvestmentPlatformSelector(state),
  sharedSettings: getSharedSettingsSelector(state),
});

const useOpenTransactionValueStyles = makeStyles(() => ({
  errorMessage: {
    display: 'flex',
    alignItems: 'center',

    '& > svg': {
      marginRight: 5
    }
  },
  subHeader: {
    margin: 0,
    fontFamily: 'Roboto-Regular',
    fontSize: 12,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: 'block',
  },
  totalValueChip: {
    height: '100%',
    textAlign: 'center',
  }
}));

function OverviewWithNewOtherAssets(props) {
  const {customSettings, reportType, sharedSettings, isCustomerApp, refreshData, refresh, isVirtualDashboard} = props;
  const classes = useStyles();
  const openTransactionClasses = useOpenTransactionValueStyles();

  const withOtherAssets = !isVirtualDashboard && _.get(props, 'dashboardSettings.with_other_assets', true);
  const withLiabilities = !isVirtualDashboard && _.get(props, 'dashboardSettings.with_liabilities', false);
  const excludedDepotId = !isVirtualDashboard && _.get(props, 'dashboardSettings.excluded_portfolios.value');

  const {
    customerId,
    portfolios,
    dates,
    dataProvider,
    withHistorical,
  } = getDataRetrievingParamsFromProps(props);

  const [data, fetch] = useDepotFinancialData(
    customerId, portfolios, dates.start, dates.end, withHistorical, excludedDepotId, dataProvider, withOtherAssets, withLiabilities);
  const unrealizedProfitAndLoss = useUnrealizedProfitAndLossData(
    customerId, portfolios, dates.start, dates.end, withHistorical, dataProvider);

  const [otherAssetsModalOpen, setOtherAssetsModalOpen] = React.useState(false);
  const defaultOtherAssetsTab = React.useRef();
  const openOtherAssetsModal = (tab) => {
    defaultOtherAssetsTab.current = tab;
    setOtherAssetsModalOpen(true);
  };

  const onOtherAssetsUpdate = () => {
    fetch();
    refreshData && refreshData();
  };

  const isExpertChartVisible = (chartName) => {
    return isExpertSectionVisible(chartName, reportType, customSettings);
  };

  const _renderTotalValue = (dataKey, additionalValue, subLine, customClasses) => {
    if (!data.data) return null;

    let value = _.get(data, dataKey) || 0;
    if(additionalValue){
      value += additionalValue;
    }

    return <AmountChip
      total={value}
      subLine={subLine}
      withValueClassName={dataKey === 'data.data.aum.aum_value' ? 'blue' : 'grey'}
      customClasses={customClasses}
    />
  };

  const getValueFraction = (value) => {
    if (value) {
      let fraction = 2;

      let openTransactionValueString = value.toString()
      let digits = openTransactionValueString.split('.')
      if (digits.length > 1) {
        let zeroNumbers = 0;
        for (let i = 0; i < digits[1].length; i++) {
          zeroNumbers++;
          if (digits[1][i] != '0') {
            break
          }
        }

        if (zeroNumbers > fraction) {
          fraction = zeroNumbers
        }
      }

      return fraction
    }

    return 2
  };

  const renderOpenTransactionValue = () => {
    let [
      unrealizedProfitAndLossData,
      unrealizedProfitAndLossDataLoading,
      unrealizedProfitAndLossDataLoadingError
    ] = [unrealizedProfitAndLoss.data || {}, unrealizedProfitAndLoss.loading, unrealizedProfitAndLoss.errors];

    if (unrealizedProfitAndLossDataLoading) {
      return <span className={openTransactionClasses.subHeader}><Skeleton /></span>
    }

    if (unrealizedProfitAndLossDataLoadingError) {
      return (
        <span className={clsx([openTransactionClasses.subHeader, openTransactionClasses.errorMessage])}>
          <ErrorOutline/>{getErrorMessage(unrealizedProfitAndLossDataLoadingError)}
        </span>
      )
    }

    const value = unrealizedProfitAndLossData.pending_switches;

    if (!_.isNil(value) && value != 0) {
      return (
        <span className={openTransactionClasses.subHeader}>
          Offene Täusche {withEuroOrDash(value || 0, getValueFraction(value))}
        </span>
      )
    }

    return null
  };

  const renderTotalInvestedAmount = () => {

    return (
      <>
        {_renderTotalValue('data.data.aum.aum_value', undefined, renderOpenTransactionValue(), openTransactionClasses)}
      </>
    )
  };

  const renderTotalInvestedAmountIncludingOtherAssets = () => {
    const openSwitches = _.get(unrealizedProfitAndLoss, 'data.pending_switches') || 0;
    return _renderTotalValue('data.data.total.value', openSwitches < 0 ? Math.abs(openSwitches) : undefined, undefined, openTransactionClasses)
  };

  const renderTransactionSaldo = () => {
    return _renderTotalValue('data.data.overall_invested_amount')
  };

  const openRiskDetails = () => {
    executeIfPathExist(props.investmentPlatform.routes, 'RISK_INDICATOR_OVERVIEW', path => {
      window.open(`/${getInvestmentDynamicPath()}${path.replace(':customer_id', customerId)}`, '_blank')
    })
  };

  const customerRisk = _.get(props, 'customerData.last_srri.srri'),
    productRisk = _.get(data, 'data.data.sri'),
    riskProfileLoading = !props.isMounted || data.loading;
  const endDateFormatted = moment(dates.end).format(DATE_FORMAT);
  const trackingStartDate = _.get(data, 'data.data.start_date');
  const withAssetsOrLiabilities = withOtherAssets || withLiabilities;
  const withSelectedAmount = isSectionVisible(customSettings, 'selectedAmount', reportType);
  const withTotalAmount = isSectionVisible(customSettings, 'totalAmount', reportType);
  const withSelectedPeriodReturn = isSectionVisible(customSettings, 'selectedPeriodReturn', reportType);
  const withTotalReturn = isSectionVisible(customSettings, 'totalReturn', reportType)

  return (
    <ChartSectionWrapper
      dataId={"overview-tab-content"}
      title={<b>Übersicht</b>}
      expanded={props.expandedItems.overviewTab}
      onExpanded={(newState) => props.onExpandedItemsChange('overviewTab', newState)}
      content={
        <Grid container spacing={2}>
          {withSelectedAmount && (
            <Grid item xs={12} md={withTotalAmount ? 6 : 12} data-id={'selectedAmount-container'}>
              <ChartSectionBordered
                title={
                  <div>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <b>Wert gewählte Depots zum {endDateFormatted}</b>
                      <DashboardInfoTooltip
                        title={"Summe der ausgewählten Depots zum gewählten Betrachtungszeitraum."}/>
                    </div>
                    <span className={classes.subHeader}>Exkl. sonstige Vermögenswerte/Verrechnungskonten</span>
                  </div>
                }
                expanded={isExpanded(props.expandedItems, 'overviewTabSections.selectedAmount')}
                onExpanded={(newState) => props.onExpandedItemsChange('overviewTabSections.selectedAmount', newState)}
                subSection
                withSubHeader
                loading={data.loading}
                error={data.error}
                displayError
                fullHeight
                content={renderTotalInvestedAmount()}
              />
            </Grid>
          )}
          {withTotalAmount && (
            <Grid item xs={12} md={withSelectedAmount ? 6 : 12} data-id={'totalAmount-container'}>
              <ChartSectionBordered
                title={
                  <div>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <b>Gesamtvermögen zum {endDateFormatted}</b>
                      <DashboardInfoTooltip
                        title={
                          `Summe aller vorhandenen Depots${withOtherAssets ? ', Verrechnungskonten und sonstigen Vermögenswerte' : ' und Verrechnungskonten'}${withLiabilities ? ' abzgl. bestehender Verbindlichkeiten' : ''} zum gewählten Betrachtungszeitraum.`
                        }
                      />
                      <BadgeForNew />
                    </div>
                    <span className={classes.subHeader}>
                      {(withOtherAssets && withLiabilities)
                          ? 'Vermögenswerte abzgl. Verbindlichkeiten'
                          :`Alle Depots inkl. Verrechnungskonten${withOtherAssets ? ' und sonstige Vermögenswerte' : ''}${withLiabilities ? ' (abzgl. Verbindlichkeiten)' : ''}`}
                    </span>
                  </div>
                }
                expanded={isExpanded(props.expandedItems, 'overviewTabSections.totalAmount')}
                onExpanded={(newState) => props.onExpandedItemsChange('overviewTabSections.totalAmount', newState)}
                subSection
                withSubHeader
                fullHeight
                loading={data.loading}
                error={data.error}
                displayError
                content={renderTotalInvestedAmountIncludingOtherAssets()}
                titleControl={withAssetsOrLiabilities ? <ArrowIconButton onClick={() => openOtherAssetsModal(MENUS.summary.id)} dataId={'assets-summary-details-btn'} /> : undefined}
              />
            </Grid>
          )}
          {withAssetsOrLiabilities && (
            <OtherAssetsSectionV2
              data={data}
              expandedItems={props.expandedItems}
              onExpandedItemsChange={props.onExpandedItemsChange}
              openModal={openOtherAssetsModal}
              withOtherAssets={withOtherAssets}
              withLiabilities={withLiabilities}
            />
          )}

          {reportType == REPORT_TYPES.DEPOT_STATUS.value ? (
            <Grid item xs={12} data-id={'transactionSaldo-container'}>
              <ChartSectionBordered
                title={<b>Transaktionssaldo</b>}
                expanded={isExpanded(props.expandedItems, 'overviewTabSections.transactionSaldo')}
                onExpanded={(newState) => props.onExpandedItemsChange('overviewTabSections.transactionSaldo', newState)}
                loading={data.loading}
                error={data.error}
                displayError
                subSection
                content={renderTransactionSaldo()}
              />
            </Grid>
          ) : (
            <>
              {withSelectedPeriodReturn && (
                <Grid item xs={12} md={withTotalReturn ? 6 : 12} data-id={'selectedPeriodReturn-container'}>
                  <ChartSectionBordered
                    title={
                      <ReturnHeader
                        title={"Rendite gewählte Depots im ausgewählten Zeitraum"}
                        subTitle={`${moment(dates.start || trackingStartDate).format(DATE_FORMAT)} - ${endDateFormatted}`}
                      />
                    }
                    expanded={isExpanded(props.expandedItems, 'overviewTabSections.selectedPeriodReturn')}
                    onExpanded={(newState) => props.onExpandedItemsChange('overviewTabSections.selectedPeriodReturn', newState)}
                    loading={data.loading}
                    error={data.error}
                    displayError
                    subSection
                    content={(
                      <AggregatedPortfolioReturnSection
                        data={data.data}
                        dataKey={'ydt'}
                        customSettings={_.get(customSettings, 'selectedPeriodReturn.subcategories')}
                        reportType={reportType}
                      />
                    )}
                  />
                </Grid>
              )}
              {withTotalReturn && (
                <Grid item xs={12} md={withSelectedPeriodReturn ? 6 : 12} data-id={'totalReturn-container'}>
                  <ChartSectionBordered
                    title={
                      <ReturnHeader
                        title={"Rendite gewählte Depots seit Investmentbeginn"}
                        subTitle={`seit ${moment(trackingStartDate).format(DATE_FORMAT)}`}
                      />
                    }
                    expanded={isExpanded(props.expandedItems, 'overviewTabSections.totalReturn')}
                    onExpanded={(newState) => props.onExpandedItemsChange('overviewTabSections.totalReturn', newState)}
                    loading={data.loading}
                    error={data.error}
                    displayError
                    subSection
                    content={(
                      <AggregatedPortfolioReturnSection
                        data={data.data}
                        dataKey={'ysb'}
                        customSettings={_.get(customSettings, 'totalReturn.subcategories')}
                        reportType={reportType}
                      />
                    )}
                  />
                </Grid>
              )}
            </>
          )}
          {!isCustomerApp && isExpertChartVisible('riskProfile') && (
            <Grid item xs={12} md={6} data-id={'riskProfile-container'}>
              <ChartSectionBordered
                title={
                  <>
                    <b>Risikoprofil</b>
                    <DashboardInfoTooltip
                      title={'Gegenüberstellung der Risikoklasse des Kunden mit dem durchschnittlichen Portfoliorisiko.'}
                    />
                  </>
                }
                titleControl={!_.isNil(customerRisk) &&
                <DetailsButton
                  onButtonClick={openRiskDetails}
                  text={'Zur Risikoklassenanalyse'}
                />
                }
                expanded={isExpanded(props.expandedItems, 'overviewTabSections.riskProfile')}
                onExpanded={(newState) => props.onExpandedItemsChange('overviewTabSections.riskProfile', newState)}
                subSection
                fullHeight
                content={<RiskProfileCard customerRisk={customerRisk} productRisk={productRisk}
                                          loading={riskProfileLoading}/>}
              />
            </Grid>
          )}
          {!isCustomerApp && isExpertChartVisible('esgProfile') && (
            <Grid item xs={12} md={6} data-id={'esgProfile-container'}>
              <ESGProfileSection
                expanded={isExpanded(props.expandedItems, 'overviewTabSections.esgProfile')}
                onExpanded={(newState) => props.onExpandedItemsChange('overviewTabSections.esgProfile', newState)}
                customerId={customerId}
                portfolios={portfolios}
                aggregatedPortfolioName={props.aggregatedPortfolioName}
              />
            </Grid>
          )}
          {isSectionVisible(customSettings, 'depots', reportType) && (
            <Grid item xs={12} data-id={'overviewTabDepots-container'}>
              <ChartSectionBordered
                dataId={"overview-depots-content"}
                title={<b>Einzeldepotübersicht</b>}
                expanded={props.expandedItems.overviewTabDepots}
                onExpanded={(newState) => props.onExpandedItemsChange('overviewTabDepots', newState)}
                content={
                  <Grid container spacing={2}>
                    {portfolios.map((portfolio) => (
                      <Grid item xs={12} md={6} key={portfolio.depotNumber} data-id={`overview-depot-container-${portfolio.depotNumber}`}>
                        <PortfolioSummarySection
                          expanded={isExpanded(props.expandedItems, ['overviewTabSections', 'depots', portfolio.depotNumber])}
                          onExpanded={(newState) => props.onExpandedItemsChange(['overviewTabSections', 'depots', portfolio.depotNumber], newState)}
                          sharedSettings={sharedSettings}
                          customerId={customerId}
                          portfolio={portfolio}
                          startDate={dates.start}
                          endDate={dates.end}
                          dataProvider={dataProvider}
                          reportType={props.reportType}
                          customSettings={_.get(customSettings, 'depots.subcategories')}
                        />
                      </Grid>
                    ))}
                  </Grid>
                }
              />
            </Grid>
          )}

          {withAssetsOrLiabilities && (
            <OtherAssetsModuleModal
              isCustomerApp={isCustomerApp}
              customerId={customerId}
              open={otherAssetsModalOpen}
              onClose={() => setOtherAssetsModalOpen(false)}
              onUpdate={onOtherAssetsUpdate}
              refresh={refresh}
              defaultTab={defaultOtherAssetsTab.current}
              startDate={dates.start}
              endDate={dates.end}
              withOtherAssets={withOtherAssets}
              withLiabilities={withLiabilities}
              excludedPortfolios={excludedDepotId}
            />
          )}
        </Grid>
      }
    />
  )
}

OverviewWithNewOtherAssets.propTypes = {};

OverviewWithNewOtherAssets.propDefs = {};

OverviewWithNewOtherAssets.defaultProps = {};

export default connect(mapStateToProps)(OverviewWithNewOtherAssets);